import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, StyleSheet, Alert, KeyboardAvoidingView, ScrollView, Platform } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import Toast from '@/components/Toast';
import PrimaryButton from '@/components/PrimaryButton';
import PageHeader from '@/components/PageHeader';
import { useKeyboardVisible } from '@/hooks/useKeyboardVisible';

export default function ChangePasswordScreen() {
  const router = useRouter();
  const { user } = useAuth();
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const keyboardVisible = useKeyboardVisible();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const [success, setSuccess] = useState(false);

  // Password validation states
  const [validations, setValidations] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumbers: false,
  });

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const hideToast = () => {
    setToastVisible(false);
  };

  const handleClose = () => {
    router.back();
  };

  const validatePassword = (password: string) => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);

    setValidations({
      minLength,
      hasUpperCase,
      hasLowerCase,
      hasNumbers,
    });

    return minLength && hasUpperCase && hasLowerCase && hasNumbers;
  };

  // Update validations when new password changes
  useEffect(() => {
    if (newPassword) {
      validatePassword(newPassword);
    }
  }, [newPassword]);

  const handleVerifyCurrentPassword = async () => {
    if (!currentPassword) {
      showToast(t('account.changePassword.passwordPlaceholder'), 'error');
      return;
    }

    setLoading(true);

    try {
      const { supabase } = await import('@/lib/supabase');

      if (!user?.email) {
        showToast(t('account.changePassword.errorUserInfo'), 'error');
        setLoading(false);
        return;
      }

      // Test current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (signInError) {
        showToast(t('account.changePassword.errorWrongPassword'), 'error');
        setLoading(false);
        return;
      }

      // If password is correct, proceed to step 2
      setStep(2);
    } catch (error) {
      console.error('Error verifying password:', error);
      showToast(t('common.error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      showToast(t('common.error'), 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showToast(t('account.changePassword.errorPasswordMismatch'), 'error');
      return;
    }

    if (!validatePassword(newPassword)) {
      showToast(t('account.changePassword.errorPasswordRequirements'), 'error');
      return;
    }

    if (newPassword === currentPassword) {
      showToast(t('account.changePassword.errorSamePassword'), 'error');
      return;
    }

    setLoading(true);

    try {
      const { supabase } = await import('@/lib/supabase');

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        showToast(t('common.error') + ': ' + updateError.message, 'error');
        setLoading(false);
        return;
      }

      // PRD §5.10 — `settings_changed` (`field=password`). Success path only.
      import('@/utils/clarityEvents')
        .then(({ logSettingsChanged }) => logSettingsChanged({ field: 'password' }))
        .catch(() => {});

      setSuccess(true);
      showToast(t('account.changePassword.successMessage'), 'success');

      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Navigate back after delay to show toast
      setTimeout(() => {
        router.back();
      }, 1500);

    } catch (error) {
      console.error('Error changing password:', error);
      showToast(t('common.error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  

  if (step === 1) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <PageHeader 
          title={t('account.changePassword.title')} 
          onBack={handleClose}
          backgroundColor="#FFFFFF"
        />

        <KeyboardAvoidingView 
          style={styles.keyboardAvoidingView} 
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
        >
          <ScrollView 
            style={styles.scrollView}
            contentContainerStyle={styles.scrollViewContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <View style={styles.content}>
              <Text style={styles.description}>
                {t('account.changePassword.subtitle')}
              </Text>

              <View style={styles.form}>
                <View style={styles.inputGroup}>
                  <Text style={styles.label}>{t('account.changePassword.currentPassword')}</Text>
                  <TextInput
                    style={[styles.input, { color: '#151515' }]}
                    value={currentPassword}
                    onChangeText={setCurrentPassword}
                    autoFocus
                    placeholder={t('account.changePassword.passwordPlaceholder')}
                    placeholderTextColor="#A19F97"
                    secureTextEntry
                    autoCapitalize="none"
                  />
                </View>
              </View>
            </View>
          </ScrollView>

          <View style={[styles.footer, { paddingBottom: keyboardVisible ? 16 : insets.bottom + 24 }]}>
            <PrimaryButton
              title={loading ? t('account.changePassword.buttonLoading') : t('common.next')}
              onPress={handleVerifyCurrentPassword}
              disabled={loading || !currentPassword}
            />
          </View>
        </KeyboardAvoidingView>

        <Toast
          visible={toastVisible}
          type={toastType}
          message={toastMessage}
          onHide={hideToast}
          duration={4000}
        />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader 
        title={t('account.changePassword.newPassword')} 
        onBack={() => setStep(1)}
        backgroundColor="#FFFFFF"
      />

      <KeyboardAvoidingView 
        style={styles.keyboardAvoidingView} 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
      >
        <ScrollView 
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.content}>
            <Text style={styles.description}>
              {t('account.changePassword.subtitle')}
            </Text>

            <View style={styles.form}>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>{t('account.changePassword.newPassword')}</Text>
                <TextInput
                  style={[styles.input, { color: '#151515' }]}
                  value={newPassword}
                  onChangeText={setNewPassword}
                  autoFocus
                  placeholder={t('account.changePassword.newPasswordPlaceholder')}
                  placeholderTextColor="#A19F97"
                  secureTextEntry
                  autoCapitalize="none"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>{t('account.changePassword.confirmPassword')}</Text>
                <TextInput
                  style={[styles.input, { color: '#151515' }]}
                  value={confirmPassword}
                  onChangeText={setConfirmPassword}
                  placeholder={t('account.changePassword.confirmPasswordPlaceholder')}
                  placeholderTextColor="#A19F97"
                  secureTextEntry
                  autoCapitalize="none"
                />
              </View>

              <View style={styles.requirementsContainer}>
                <Text style={styles.requirementsTitle}>{t('account.changePassword.requirements')}</Text>

                <View style={styles.requirementsList}>
                  <View style={styles.requirementItem}>
                    <View style={[
                      styles.checkbox,
                      validations.minLength && styles.checkboxActive
                    ]}>
                      {validations.minLength && (
                        <Svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                          <Path 
                            d="M6.33268 2.396L3.12435 5.60433L1.66602 4.146" 
                            stroke="white" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                          />
                        </Svg>
                      )}
                    </View>
                    <Text style={styles.requirementText}>{t('account.changePassword.requirement1')}</Text>
                  </View>

                  <View style={styles.requirementItem}>
                    <View style={[
                      styles.checkbox,
                      validations.hasUpperCase && styles.checkboxActive
                    ]}>
                      {validations.hasUpperCase && (
                        <Svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                          <Path 
                            d="M6.33268 2.396L3.12435 5.60433L1.66602 4.146" 
                            stroke="white" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                          />
                        </Svg>
                      )}
                    </View>
                    <Text style={styles.requirementText}>{t('account.changePassword.requirement2')}</Text>
                  </View>

                  <View style={styles.requirementItem}>
                    <View style={[
                      styles.checkbox,
                      validations.hasLowerCase && styles.checkboxActive
                    ]}>
                      {validations.hasLowerCase && (
                        <Svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                          <Path 
                            d="M6.33268 2.396L3.12435 5.60433L1.66602 4.146" 
                            stroke="white" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                          />
                        </Svg>
                      )}
                    </View>
                    <Text style={styles.requirementText}>{t('account.changePassword.requirement3')}</Text>
                  </View>

                  <View style={styles.requirementItem}>
                    <View style={[
                      styles.checkbox,
                      validations.hasNumbers && styles.checkboxActive
                    ]}>
                      {validations.hasNumbers && (
                        <Svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                          <Path 
                            d="M6.33268 2.396L3.12435 5.60433L1.66602 4.146" 
                            stroke="white" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                          />
                        </Svg>
                      )}
                    </View>
                    <Text style={styles.requirementText}>{t('account.changePassword.requirement4')}</Text>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </ScrollView>

        <View style={[styles.footer, { paddingBottom: keyboardVisible ? 16 : insets.bottom + 24 }]}>
          <PrimaryButton
            title={loading ? t('account.changePassword.buttonLoading') : t('account.changePassword.button')}
            onPress={handleChangePassword}
            disabled={loading}
          />
        </View>
      </KeyboardAvoidingView>

      <Toast
        visible={toastVisible}
        type={toastType}
        message={toastMessage}
        onHide={hideToast}
        duration={4000}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F0EEE4',
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
  },
  placeholder: {
    width: 24,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  description: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 28.8,
    marginBottom: 32,
  },
  form: {
    gap: 24,
  },
  inputGroup: {
    gap: 8,
  },
  label: {
    color: '#151515',
    fontFamily: 'Inter',
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 14,
  },
  input: {
    borderWidth: 1,
    borderColor: '#E7EAED',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    backgroundColor: '#FFFFFF',
  },
  requirementsContainer: {
    padding: 24,
    alignItems: 'flex-start',
    gap: 20,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#F9F8F3',
    borderRadius: 16,
    flexDirection: 'column',
    width: '100%',
  },
  requirementsTitle: {
    color: '#000000',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 14,
  },
  requirementsList: {
    alignItems: 'flex-start',
    gap: 8,
    flexDirection: 'column',
    width: '100%',
  },
  requirementItem: {
    alignItems: 'center',
    gap: 8,
    width: '100%',
    height: 24,
    flexDirection: 'row',
  },
  checkbox: {
    aspectRatio: 1,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#7E7D75',
    backgroundColor: '#FFFFFF',
    width: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  checkboxActive: {
    borderWidth: 3,
    borderColor: '#0FA971',
    backgroundColor: '#0FA971',
    padding: 4,
  },
  requirementText: {
    color: '#151515',
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 19.6,
  },
  footer: {
    paddingHorizontal: 24,
    paddingTop: 8,
  },
  
});